import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Radio, Eye, Ticket } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";
import LiveNowPanel from "../components/LiveNowPanel";
import { displayName } from "../lib/displayName";

export function LivestreamList() {
  const { user, profile } = useAuth();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", ticketPriceUSD: 0, coverImageUrl: "" });
  const load = async () => { const { data } = await api.get("/livestreams"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/livestreams", { ...form, ticketPriceUSD: parseFloat(form.ticketPriceUSD) || 0 });
      setShow(false); setForm({ title: "", description: "", ticketPriceUSD: 0, coverImageUrl: "" });
      load(); toast.success("Livestream created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Practitioners and admins can host livestreams.
  const canHost = (user?.role === "PROVIDER" || user?.role === "ADMIN") && profile?.subscriptionStatus === "active";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mystic">Live Ceremonies</div>
            <h1 className="font-serif text-5xl mt-2">Livestreams</h1>
          </div>
          {canHost && (
            <button onClick={() => setShow(!show)} className="bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs" data-testid="livestream-create-toggle">+ New</button>
          )}
        </div>

        {/* Live Now — instant free broadcasts. Hides itself when nobody is live so
            the scheduled section stays the focus during quiet hours. */}
        <LiveNowPanel heading="Live Now" hideWhenEmpty />
        {show && (
          <div className="parchment p-6 space-y-3">
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="ls-title" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="ls-desc" />
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="label-mystic">Ticket Price USD (0 = free)</label>
                <input type="number" step="0.01" value={form.ticketPriceUSD} onChange={(e) => setForm({...form, ticketPriceUSD: e.target.value})} className="w-full bg-input border border-secondary/20 p-3 rounded-sm mt-1" data-testid="ls-price" />
              </div>
              <ImageUploader value={form.coverImageUrl} onChange={(v) => setForm({...form, coverImageUrl: v})} testid="ls-cover" label="Cover" />
            </div>
            <button onClick={create} className="bg-primary text-primary-foreground px-6 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid="ls-create-btn">Create</button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((l) => (
            <Link to={`/livestreams/${l.id}`} key={l.id} className="parchment p-6 hover:border-secondary/40 transition" data-testid={`ls-${l.id}`}>
              {l.coverImageUrl && <img src={l.coverImageUrl} alt="" className="w-full h-40 object-cover border border-secondary/15 mb-4" />}
              <div className="flex items-center justify-between mb-2">
                <span className="label-mystic">{displayName({ ...(l.hostProfile || {}), user: l.host })}</span>
                <span className={`text-[10px] px-2 py-1 uppercase tracking-wider ${l.status==='live'?"bg-primary/20 text-primary":"text-foreground/60"}`}>{l.status}</span>
              </div>
              <h3 className="font-serif text-2xl mb-2">{l.title}</h3>
              <p className="text-foreground/70 text-sm line-clamp-2">{l.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="font-serif text-2xl text-secondary">{l.ticketPriceUSD > 0 ? `$${l.ticketPriceUSD.toFixed(2)}` : "Free"}</div>
                <span className="text-xs text-foreground/50">{l.ticketsSold || 0} tickets</span>
              </div>
            </Link>
          ))}
          {items.length === 0 && <div className="parchment p-12 text-center text-foreground/50 italic col-span-2">No livestreams yet.</div>}
        </div>
      </div>
    </Layout>
  );
}

export function LivestreamDetail() {
  const { lid } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [going, setGoing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/livestreams/${lid}`); setItem(data); }
    catch { toast.error("Not found"); nav("/livestreams"); }
  };
  useEffect(() => { load(); }, [lid]);
  useEffect(() => {
    if (!sessionId) return;
    const poll = async (n=0) => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Ticket confirmed"); await refresh(); load(); return; }
        if (n >= 8) return;
        setTimeout(() => poll(n+1), 2000);
      } catch {}
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const buy = async () => {
    setGoing(true);
    try {
      const { data } = await api.post("/checkout/init", {
        origin: window.location.origin, kind: "livestream_ticket", livestreamId: lid,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setGoing(false); }
  };

  if (!item) return <Layout><div className="parchment p-12 text-center">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="parchment overflow-hidden">
        {item.coverImageUrl && <img src={item.coverImageUrl} alt="" className="w-full h-64 object-cover" />}
        <div className="p-8">
          <div className="label-mystic">Live Broadcast — {displayName({ ...(item.hostProfile || {}), user: item.host })}</div>
          <h1 className="font-serif text-5xl mt-2 mb-4">{item.title}</h1>
          <p className="text-foreground/80 mb-6">{item.description}</p>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="font-serif text-4xl text-secondary">{item.ticketPriceUSD > 0 ? `$${item.ticketPriceUSD.toFixed(2)}` : "Free"}</div>
            {item.hasTicket || item.hostId === user?.id ? (
              <button onClick={() => nav(`/live/${item.hostId}`)} className="bg-primary text-primary-foreground px-8 py-3 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2" data-testid="ls-join">
                <Radio size={14} strokeWidth={1.25} /> {item.hostId === user?.id ? "Start Broadcasting" : "Join Live"}
              </button>
            ) : (
              <button onClick={buy} disabled={going} className="bg-primary text-primary-foreground px-8 py-3 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2 disabled:opacity-50" data-testid="ls-buy">
                <Ticket size={14} strokeWidth={1.25} /> {going ? "..." : "Buy Ticket"}
              </button>
            )}
          </div>
          <div className="mt-6 label-mystic flex items-center gap-2"><Eye size={12} strokeWidth={1.25} /> {item.ticketsSold || 0} confirmed</div>
        </div>
      </div>
    </Layout>
  );
}
